import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Loader2, Calendar, Users, Phone,
  Heart, Building2, Baby, PartyPopper, UtensilsCrossed, Truck, Bell, Filter,
  AlertTriangle, Sparkles, RefreshCw, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_COLORS = {
  Mariage: { bg: 'bg-pink-500', dot: '#ec4899', label: 'Mariage' },
  Entreprise: { bg: 'bg-blue-500', dot: '#3b82f6', label: 'Entreprise' },
  Bapteme: { bg: 'bg-sky-500', dot: '#0ea5e9', label: 'Bapteme' },
  Anniversaire: { bg: 'bg-amber-500', dot: '#f59e0b', label: 'Anniversaire' },
  Restaurant: { bg: 'bg-emerald-500', dot: '#10b981', label: 'Restaurant' },
  Traiteur: { bg: 'bg-orange-500', dot: '#f97316', label: 'Traiteur' },
  relance: { bg: 'bg-purple-500', dot: '#a855f7', label: 'Relance' },
};

const TYPE_ICONS = { Mariage: Heart, Entreprise: Building2, Bapteme: Baby, Anniversaire: PartyPopper, Restaurant: UtensilsCrossed, Traiteur: Truck, relance: Bell };

const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

export default function CalendrierCommercial({ onOpenRdv }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [iaAgenda, setIaAgenda] = useState(null);
  const [loadingIA, setLoadingIA] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/propositions/calendrier/${year}/${month}`);
      setData(await r.json());
    } catch {}
    setLoading(false);
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const loadIaAgenda = async () => {
    setLoadingIA(true);
    try {
      const r = await fetch(`${API}/api/propositions/calendrier/ia-agenda`);
      if (r.ok) setIaAgenda(await r.json());
    } catch {}
    setLoadingIA(false);
  };

  const syncCalendar = async () => {
    setSyncing(true);
    try {
      const r = await fetch(`${API}/api/propositions/calendrier/sync-main`);
      if (r.ok) { const d = await r.json(); toast.success(`${d.synced} evenement(s) synchronise(s)`); }
    } catch { toast.error('Erreur sync'); }
    setSyncing(false);
  };

  const monthLabel = new Date(year, month - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  // Build calendar grid
  const firstDay = new Date(year, month - 1, 1);
  let startDow = firstDay.getDay(); // 0=Sun
  startDow = startDow === 0 ? 6 : startDow - 1; // Convert to Mon=0
  const daysInMonth = new Date(year, month, 0).getDate();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Group events by date
  const eventsByDate = {};
  if (data?.events) {
    const filtered = filterType ? data.events.filter(e => e.type === filterType) : data.events;
    for (const evt of filtered) {
      const d = evt.date;
      if (!eventsByDate[d]) eventsByDate[d] = [];
      eventsByDate[d].push(evt);
    }
  }

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="space-y-4" data-testid="calendrier-commercial">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-[rgb(var(--lm-gold))]" />
          <h2 className="text-sm font-bold text-[rgb(var(--lm-text))]">Calendrier Commercial</h2>
        </div>
        <div className="flex gap-1.5">
          <Button onClick={loadIaAgenda} disabled={loadingIA} size="sm" variant="outline" className="text-[9px] h-7 px-2 border-[rgb(var(--lm-border))]" data-testid="ia-agenda-btn">
            {loadingIA ? <Loader2 size={10} className="animate-spin mr-1" /> : <Sparkles size={10} className="mr-1" />}
            IA Agenda
          </Button>
          <Button onClick={syncCalendar} disabled={syncing} size="sm" variant="outline" className="text-[9px] h-7 px-2 border-[rgb(var(--lm-border))]" data-testid="sync-calendar-btn">
            {syncing ? <Loader2 size={10} className="animate-spin mr-1" /> : <RefreshCw size={10} className="mr-1" />}
            Sync
          </Button>
        </div>
      </div>

      {/* Month nav */}
      <div className="flex items-center justify-between bg-[rgb(var(--lm-card))] rounded-xl p-3 border border-[rgb(var(--lm-border))]/30">
        <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-bg))]"><ChevronLeft size={16} className="text-[rgb(var(--lm-text))]" /></button>
        <span className="text-sm font-bold text-[rgb(var(--lm-text))] capitalize">{monthLabel}</span>
        <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-bg))]"><ChevronRight size={16} className="text-[rgb(var(--lm-text))]" /></button>
      </div>

      {/* Stats */}
      {data?.stats && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          <div className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/20 rounded-lg px-3 py-2 text-center shrink-0">
            <p className="text-lg font-bold text-[rgb(var(--lm-gold))]">{data.stats.total_events}</p>
            <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">EVENEMENTS</p>
          </div>
          <div className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/20 rounded-lg px-3 py-2 text-center shrink-0">
            <p className="text-lg font-bold text-[rgb(var(--lm-text))]">{data.stats.total_couverts}</p>
            <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">COUVERTS</p>
          </div>
          <div className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/20 rounded-lg px-3 py-2 text-center shrink-0">
            <p className="text-lg font-bold text-purple-500">{data.stats.total_relances}</p>
            <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">RELANCES</p>
          </div>
        </div>
      )}

      {/* IA Agenda alerts & suggestions */}
      {iaAgenda && (
        <div className="space-y-2" data-testid="ia-agenda-results">
          {iaAgenda.alerts?.length > 0 && (
            <div className="bg-red-50/50 dark:bg-red-900/10 border border-red-200/30 rounded-xl p-3">
              <p className="text-[9px] font-bold text-red-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                <AlertTriangle size={10} /> Alertes
              </p>
              <div className="space-y-1">
                {iaAgenda.alerts.map((a, i) => (
                  <p key={i} className={`text-[10px] flex items-start gap-1.5 ${a.level === 'danger' ? 'text-red-600' : a.level === 'warning' ? 'text-amber-600' : 'text-blue-600'}`}>
                    <span className={`w-2 h-2 rounded-full shrink-0 mt-0.5 ${a.level === 'danger' ? 'bg-red-500' : a.level === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                    {a.message}
                  </p>
                ))}
              </div>
            </div>
          )}
          {iaAgenda.suggestions?.length > 0 && (
            <div className="bg-purple-50/50 dark:bg-purple-900/10 border border-purple-200/30 rounded-xl p-3">
              <p className="text-[9px] font-bold text-purple-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Sparkles size={10} /> Suggestions IA
              </p>
              <div className="space-y-1">
                {iaAgenda.suggestions.map((s, i) => (
                  <p key={i} className="text-[10px] text-purple-700 dark:text-purple-300 flex items-start gap-1.5">
                    <Info size={9} className="shrink-0 mt-0.5 text-purple-400" /> {s}
                  </p>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2 text-[8px] text-[rgb(var(--lm-text-muted))]">
            <span>{iaAgenda.stats?.total_upcoming || 0} evenement(s) a venir</span>
            <span>{iaAgenda.stats?.total_couverts_upcoming || 0} couverts total</span>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        <button onClick={() => setFilterType('')}
          className={`text-[9px] px-2 py-1 rounded-full shrink-0 ${!filterType ? 'bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))] font-bold' : 'bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text-muted))]'}`}>
          Tous
        </button>
        {Object.entries(TYPE_COLORS).filter(([k]) => k !== 'relance').map(([type, c]) => (
          <button key={type} onClick={() => setFilterType(type === filterType ? '' : type)}
            className={`text-[9px] px-2 py-1 rounded-full shrink-0 flex items-center gap-1 ${filterType === type ? 'font-bold text-white' : 'bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text-muted))]'}`}
            style={filterType === type ? { backgroundColor: c.dot } : {}}>
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: c.dot }} />
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[rgb(var(--lm-text-muted))]" /></div>
      ) : (
        <>
          {/* Calendar grid */}
          <div className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-xl overflow-hidden">
            <div className="grid grid-cols-7 border-b border-[rgb(var(--lm-border))]/20">
              {JOURS.map(j => (
                <div key={j} className="text-center py-2 text-[9px] font-bold text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">{j}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {cells.map((day, i) => {
                if (!day) return <div key={i} className="h-16 border-b border-r border-[rgb(var(--lm-border))]/10" />;
                const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const evts = eventsByDate[dateStr] || [];
                const isToday = dateStr === todayStr;

                return (
                  <div key={i} className={`h-16 border-b border-r border-[rgb(var(--lm-border))]/10 p-0.5 ${isToday ? 'bg-[rgb(var(--lm-gold))]/5' : ''}`}>
                    <div className={`text-[10px] text-right pr-1 ${isToday ? 'font-bold text-[rgb(var(--lm-gold))]' : 'text-[rgb(var(--lm-text-muted))]'}`}>{day}</div>
                    <div className="space-y-0.5">
                      {evts.slice(0, 2).map((evt, j) => {
                        const tc = TYPE_COLORS[evt.type] || TYPE_COLORS.Entreprise;
                        return (
                          <div key={j} onClick={() => onOpenRdv && onOpenRdv(evt.id)}
                            className="flex items-center gap-0.5 px-0.5 rounded cursor-pointer hover:opacity-80"
                            style={{ backgroundColor: tc.dot + '20' }}>
                            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: tc.dot }} />
                            <span className="text-[7px] truncate" style={{ color: tc.dot }}>{evt.nom?.split(' ')[0]}</span>
                            {evt.nb_personnes > 0 && <span className="text-[6px] text-[rgb(var(--lm-text-muted))]">{evt.nb_personnes}</span>}
                          </div>
                        );
                      })}
                      {evts.length > 2 && <span className="text-[7px] text-[rgb(var(--lm-text-muted))] pl-1">+{evts.length - 2}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Upcoming events list */}
          {data?.events?.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Prochains evenements</p>
              {(filterType ? data.events.filter(e => e.type === filterType) : data.events).map((evt, i) => {
                const tc = TYPE_COLORS[evt.type] || TYPE_COLORS.Entreprise;
                const Ic = TYPE_ICONS[evt.type] || Calendar;
                return (
                  <div key={i} onClick={() => onOpenRdv && onOpenRdv(evt.id)}
                    className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/20 rounded-lg px-3 py-2 flex items-center gap-3 cursor-pointer hover:shadow-sm"
                    data-testid={`cal-event-${evt.id}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: tc.dot + '15' }}>
                      <Ic size={14} style={{ color: tc.dot }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold text-[rgb(var(--lm-text))] truncate">{evt.nom}</p>
                      <div className="flex items-center gap-2 text-[8px] text-[rgb(var(--lm-text-muted))]">
                        <span>{new Date(evt.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</span>
                        {evt.nb_personnes > 0 && <span className="flex items-center gap-0.5"><Users size={8} /> {evt.nb_personnes}</span>}
                        <span className="px-1 py-0.5 rounded" style={{ backgroundColor: tc.dot + '15', color: tc.dot }}>{evt.type}</span>
                      </div>
                    </div>
                    <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${evt.statut === 'Confirme' || evt.statut === 'confirme' ? 'bg-green-100 text-green-700' : evt.statut === 'relance' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                      {evt.statut}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
